<template>
  <ontos-form-panel v-bind="heading" :loaded="loaded" :saving="isSaving"
                    :save-disabled="!hasChanges" @save="save">
    <!-- Slot com ESCOPO: quem hospeda decide os controles — a janela põe voltar e expandir, a
         página põe voltar —, mas o `dirty` nasce aqui dentro, do diff, e é ele que faz a saída
         pedir confirmação. Sem o escopo, o host teria de refazer a conta. -->
    <template v-if="$slots.controls" #controls>
      <!-- O rascunho vai no escopo junto do `dirty`: quem oferece a saída para a página
           inteira precisa dele para levá-lo, e sem isso Expandir recomeçaria do zero. E o
           original junto, que é contra o que o rascunho se mede (ver a prop `original`). -->
      <slot name="controls" :dirty="hasChanges" :draft="instance" :original="originalInstance"/>
    </template>

    <v-form ref="form">
      <ontos-instance-input ref="input" :idea="idea" :instance="instance" :domains="domains"
                            :context="context" :choice-domains="choiceDomains"
                            :reverse-info="reverseInfo" :original="originalInstance"
                            @showMessage="$emit('showMessage', $event)"/>
    </v-form>
  </ontos-form-panel>

  <!-- Irmão do painel, e não filho: dentro de uma janela ele precisa estar na árvore do
       v-dialog, que é por onde o Vuetify descobre os aninhados. -->
  <ontos-access-loss-dialog v-if="pendingLoss" :loss="pendingLoss"
                            @confirmed="confirmLoss" @cancelled="pendingLoss = null"/>
</template>
<script>
import {defineAsyncComponent} from "vue";
import LoadableComponent from "@/components/shared/LoadableComponent";
import FormPanel from "@/components/shared/FormPanel.vue";
import {deepCopy} from "@/utils/copy";
import EditInstanceService from "@/services/EditInstanceService";
import {seedReverseValues} from "@/services/reverseRelationship";
import {instanceEdit} from "@/utils/heading";
import {hasAccessRules} from "@/types/Idea";
import {previewFromSaved} from "@/utils/instancePreview";

/**
 * A edição de uma instância: o formulário, o que conta como alteração, o aviso de perder o acesso e a
 * gravação. **Sem janela em volta** — quem a hospeda decide se é um modal ou uma página.
 *
 * A confirmação de "vínculo roubado" ("Mover itens?") saiu: não se rouba mais o item de outro pela lista
 * reversa — ela nem o oferece (ver o `freeToLink`).
 *
 * É o par do `InstanceView`, que o lado da visualização já tinha. A edição não tinha: a página
 * e o modal eram duas implementações paralelas, e elas divergiram — só o modal confirmava a
 * transferência de vínculo reverso e só ele traduzia os erros de validação do servidor. A
 * página gravava direto. Com um leaf só, os dois caminhos passam a fazer a mesma coisa.
 *
 * Recebe a instância já carregada: quem sabe de onde ela vem é o hospedeiro — a página lê do
 * servidor, a janela recebe a que já estava na tela.
 */
export default {
  emits: ['saved', 'showMessage', 'showErrorMessage'],
  extends: LoadableComponent,
  props: {
    context: {type: Object, required: true},
    idea: Object,
    instance: Object,
    //os candidatos dos relacionamentos
    domains: Object,
    //os domínios dos atributos de seleção — o `domains` acima é o de relacionamento
    choiceDomains: {type: Array, default: () => []},
    reverseInfo: Object,
    /**
     * O item como estava gravado, quando a `instance` chega já mexida — o Expandir da janela, que
     * leva o rascunho para a página. Sem ele a página tomava o rascunho por ponto de partida (relato
     * do usuário): o Salvar desabilitava, salvar mandava só o que se mexeu depois, e a semeadura dos
     * reversos desfazia o que se mexeu neles na janela.
     */
    original: {type: Object, default: null}
  },

  components: {
    'ontos-form-panel': FormPanel,
    'ontos-instance-input': defineAsyncComponent(() => import('./InstanceInput.vue')),
    'ontos-access-loss-dialog': defineAsyncComponent(() => import('./AccessLossDialog.vue'))
  },

  data: function () {
    return {
      originalInstance: {},
      service: undefined,
      //O que a edição tiraria de quem a faz, à espera da confirmação — ver `accessLoss`
      pendingLoss: null,
      //Confirmou gravar o que não verá mais: quem hospeda não volta para a ficha, que seria um 404
      hiding: false
    }
  },

  computed: {
    /** O mesmo cabeçalho da página de editar: a regra mora no utils/heading */
    heading() {
      //O id vem do contexto, que é quem sabe qual item está aberto: o documento carregado não
      //traz `_instance`, e é dele que sai a cor do acento.
      return this.idea
          ? instanceEdit(this.instance, this.idea, this.context.instance)
          : {title: '', subtitle: ''}
    },

    /** Enquanto não carregou não há service, e nada foi editado ainda */
    hasChanges() {
      return !!this.service
          && Object.entries(this.service.instanceDiff(this.originalInstance, this.instance)).length > 0
    }
  },

  methods: {
    async load() {
      //Com o original, o rascunho já foi semeado onde começou: semear de novo o reescreveria pelo
      //`_rels`, que é o gravado
      if (this.original) this.originalInstance = deepCopy(this.original)
      else {
        //Semeia os aliases reversos a partir de _rels ANTES do snapshot: sem isso o
        //originalInstance guarda undefined e todo campo reverso parece sujo.
        seedReverseValues(this.instance, this.idea)
        this.originalInstance = deepCopy(this.instance)
      }
      this.service = new EditInstanceService(this.context, this.$instances, this.idea)
    },

    showMessage() {
      this.info(this.$tt('info.saved'))
    },

    async save() {
      const validForm = await this.$refs.input.validateForm()
      if (!validForm) return

      //A confirmação fica fora do saving() para o overlay não cobrir o diálogo
      this.hiding = false
      const loss = await this.accessLoss()
      if (loss) {
        this.pendingLoss = loss
        return
      }
      await this.commit()
    },

    async confirmLoss() {
      this.hiding = this.pendingLoss === 'visible'
      this.pendingLoss = null
      await this.commit()
    },

    /**
     * O que esta edição tiraria de quem a faz — a vista do item ou a edição dele —, pela prévia:
     * só o servidor avalia as permissões, e sobre o item como ele ficaria (pedido do usuário).
     *
     * Só na coleção com permissões, e só ao editar: criar o que não se verá o servidor já recusa.
     * Uma prévia que falha não trava o salvar — o aviso é conveniência, e o servidor grava o que é
     * permitido de todo modo.
     */
    async accessLoss() {
      if (!this.context.instance || !hasAccessRules(this.idea)) return null
      try {
        const {access} = await this.$instances.preview({
          systemAlias: this.context.system, ideaAlias: this.idea.alias, instanceId: this.context.instance
        }, this.service.instanceDiff(this.originalInstance, this.instance))
        if (!access) return null
        return !access.visible ? 'visible' : !access.editable ? 'editable' : null
      } catch (err) {
        console.warn('Não foi possível conferir o acesso depois de salvar', err)
        return null
      }
    },

    async commit() {
      await this.saving(async () => {
        try {
          await this.service.update(this.originalInstance, this.instance)
        } catch (err) {
          //saveAnd nunca teve try/catch e o errorCaptured do LoadableComponent não
          //pega promise rejeitada de @click: um 400 morria no console.
          //
          //As falhas de campo vão para os campos; o resto, para a janela de recusa — ver `refuse`
          this.$refs.input.refuse(err)
          return
        }
        this.showMessage()
        //A prévia da ficha a que se volta (ver `previewFromSaved`): o original, que este formulário guardou
        //intacto, com o que o rascunho trocou. `undefined` quando o título mudaria — a ficha então lê
        const snapshot = {value: this.originalInstance, relDomains: this.domains, reverseInfo: this.reverseInfo}
        this.$emit('saved', this.instance, {
          hidden: this.hiding, preview: previewFromSaved(snapshot, this.instance, this.idea, this.domains)
        })
        this.$refs.form.resetValidation()
      })
    }
  }
}
</script>
